import { enforceRateLimit } from "./rate-limit.server";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const attachmentSchema = z
  .array(z.object({ base64: z.string().max(9_000_000), mimeType: z.string().max(60) }))
  .max(3)
  .default([]);

const ticketSchema = z.object({
  category: z.string().min(1).max(40),
  subject: z.string().trim().min(3, "Skriv en kort rubrik.").max(140),
  body: z.string().trim().min(10, "Beskriv gärna lite mer så kan vi hjälpa dig snabbare.").max(5000),
  context: z.record(z.string(), z.unknown()).default({}),
  attachments: attachmentSchema,
});

export type SupportTicketRow = {
  id: string;
  ticket_no: number;
  subject: string;
  category: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
};

export type SupportMessageRow = {
  id: string;
  author_kind: string;
  author_name: string;
  body: string;
  attachments: string[];
  created_at: string;
};

/** Skickar in ett ärende som inloggad. */
export const submitTicket = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ticketSchema.parse(input))
  .handler(async ({ data, context }) => {
    await enforceRateLimit("support_ticket", context.userId);
    const { createTicket, uploadSupportImage } = await import("@/lib/support.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: userRes } = await supabaseAdmin.auth.admin.getUserById(context.userId);
    const email = userRes?.user?.email ?? "";
    if (!email) throw new Error("Vi hittar ingen e-postadress på ditt konto.");
    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("display_name")
      .eq("user_id", context.userId)
      .maybeSingle();

    const paths: string[] = [];
    for (const a of data.attachments) paths.push(await uploadSupportImage(a.base64, a.mimeType));

    const ticket = await createTicket({
      userId: context.userId,
      email,
      name: (profile?.display_name as string | undefined) ?? "",
      category: data.category,
      subject: data.subject,
      body: data.body,
      context: data.context,
      attachments: paths,
    });
    return { ticketNo: ticket.ticketNo };
  });

/** Begär en bekräftelsekod för den som inte är inloggad. */
export const requestSupportCode = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ email: z.string().trim().email().max(200) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { issueGuestCode } = await import("@/lib/support.server");
    await issueGuestCode(data.email);
    return { ok: true };
  });

/** Skickar in ett ärende utan inloggning – kräver giltig kod. */
export const submitGuestTicket = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    ticketSchema
      .extend({
        email: z.string().trim().email().max(200),
        name: z.string().trim().max(80).default(""),
        code: z.string().trim().min(3).max(10),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { createTicket, uploadSupportImage, verifyGuestCode } = await import(
      "@/lib/support.server"
    );
    const check = await verifyGuestCode(data.email, data.code);
    if (!check.ok) throw new Error(check.message);

    const paths: string[] = [];
    for (const a of data.attachments) paths.push(await uploadSupportImage(a.base64, a.mimeType));

    const ticket = await createTicket({
      userId: null,
      email: data.email,
      name: data.name,
      category: data.category,
      subject: data.subject,
      body: data.body,
      context: data.context,
      attachments: paths,
    });
    return { ticketNo: ticket.ticketNo };
  });

/** Mina ärenden med senaste meddelande. */
export const getMyTickets = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("support_tickets")
      .select("id, ticket_no, subject, category, status, priority, created_at, updated_at")
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return (data ?? []) as SupportTicketRow[];
  });

/** Hela tråden i ett av mina ärenden (interna anteckningar filtreras bort av databasen). */
export const getMyTicket = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: ticket, error } = await context.supabase
      .from("support_tickets")
      .select("id, ticket_no, subject, category, status, priority, created_at, updated_at")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!ticket) throw new Error("Ärendet hittades inte.");
    const { data: messages } = await context.supabase
      .from("support_messages")
      .select("id, author_kind, author_name, body, attachments, created_at")
      .eq("ticket_id", data.id)
      .order("created_at", { ascending: true });
    return {
      ticket: ticket as SupportTicketRow,
      messages: (messages ?? []) as SupportMessageRow[],
    };
  });

/** Kundens svar i ett eget ärende. */
export const replyToMyTicket = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid(), body: z.string().trim().min(1).max(5000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("support_messages").insert({
      ticket_id: data.id,
      author_kind: "customer",
      author_id: context.userId,
      body: data.body,
    });
    if (error) throw new Error(error.message);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("support_tickets")
      .update({ status: "open", last_customer_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("user_id", context.userId);
    return { ok: true };
  });

/** Gästens tråd via den hemliga länken i mejlet. */
export const getGuestTicket = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().trim().min(20).max(120) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: ticket } = await supabaseAdmin
      .from("support_tickets")
      .select("id, ticket_no, subject, category, status, priority, created_at, updated_at")
      .eq("access_token", data.token)
      .maybeSingle();
    if (!ticket) throw new Error("Länken gäller inte längre.");
    const { data: messages } = await supabaseAdmin
      .from("support_messages")
      .select("id, author_kind, author_name, body, attachments, created_at")
      .eq("ticket_id", ticket.id)
      .eq("internal", false)
      .order("created_at", { ascending: true });
    return {
      ticket: ticket as unknown as SupportTicketRow,
      messages: (messages ?? []) as unknown as SupportMessageRow[],
    };
  });

/** Gästens svar via den hemliga länken. */
export const replyGuestTicket = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        token: z.string().trim().min(20).max(120),
        body: z.string().trim().min(1).max(5000),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: ticket } = await supabaseAdmin
      .from("support_tickets")
      .select("id, name, email")
      .eq("access_token", data.token)
      .maybeSingle();
    if (!ticket) throw new Error("Länken gäller inte längre.");
    await supabaseAdmin.from("support_messages").insert({
      ticket_id: ticket.id,
      author_kind: "customer",
      author_name: (ticket.name as string) || (ticket.email as string),
      body: data.body,
    });
    await supabaseAdmin
      .from("support_tickets")
      .update({ status: "open", last_customer_at: new Date().toISOString() })
      .eq("id", ticket.id);
    return { ok: true };
  });
